import { computed, inject, Injectable, signal } from '@angular/core';
import { UserService } from 'app/core/user/service/user.service';
import { catchError, finalize, forkJoin, map, Observable, of, shareReplay, Subject, tap } from 'rxjs';
import { PanelApiService } from './panel-api.service';
import { CaseEvent, CaseSummary, Company, Financing, Me, Owner, PanelDocument, Policy, RiskCategory, TicketSummary, Vehicle } from './panel.models';

/**
 * Dane klienta wspólne dla ekranów (powłoka, strona główna, listy). Ładowane raz na sesję
 * i przeładowywane, gdy zmieni się klient (podgląd administratora).
 *
 * Filtr firmy (Issue 26): na koncie z firmami powiązanymi `vehicles`, `policies`, `financing`, `cases`, `events`
 * i `documents` zawierają tylko pozycje wybranej firmy (listy, kafle, terminy, dzwonek, wyszukiwarka);
 * `all*` — wszystkie pozycje konta (strony szczegółów, linki między pozycjami).
 *
 * Odświeżanie (Issue 36): dane są ważne {@link MAX_AGE_MS}; starsze odświeżamy w tle — przy wejściu na stronę,
 * po powrocie do karty i co minutę przy karcie na wierzchu (powłoka). Ekran nie miga „Wczytuję…”, a błąd
 * odświeżenia zostawia poprzednie dane. Po każdym odświeżeniu `refreshed$` — strona sprawy pobiera ją ponownie.
 */
/** Ważność danych list (Issue 36). */
export const MAX_AGE_MS = 60_000;

@Injectable({ providedIn: 'root' })
export class PanelStore {
    private readonly api = inject(PanelApiService);
    private readonly userService = inject(UserService);

    readonly me = signal<Me | null>(null);
    readonly allVehicles = signal<Vehicle[] | null>(null);
    readonly allPolicies = signal<Policy[] | null>(null);
    readonly allFinancing = signal<Financing[] | null>(null);
    readonly allCases = signal<CaseSummary[] | null>(null);
    /** Ostatnie zdarzenia spraw (dzwonek, „Co ostatnio się wydarzyło”). */
    readonly allEvents = signal<CaseEvent[] | null>(null);
    /** Zgłoszenia (Issue 40) — dolna część listy „Sprawy”, zakładki przy aucie, polisie, umowie i sprawie. */
    readonly allTickets = signal<TicketSummary[] | null>(null);
    readonly failed = signal(false);
    /** Słownik ryzyk (Issue 17) — wczytywany przy pierwszym oknie szczegółów ryzyka. */
    readonly risks = signal<RiskCategory[] | null>(null);
    /** Dokumenty (Issue 13) — wczytywane przy pierwszej potrzebie: ekran Dokumenty, pojazd, wyszukiwarka. */
    readonly allDocuments = signal<PanelDocument[] | null>(null);
    readonly documentsFailed = signal(false);

    /** Firmy konta (Issue 26); filtr i etykiety tylko przy więcej niż jednej. */
    readonly companies = computed<Company[]>(() => this.me()?.companies ?? []);
    readonly multiCompany = computed(() => this.companies().length > 1);
    /** Wybrana firma (`?firma=`) albo null — wszystkie firmy. */
    readonly company = signal<number | null>(null);

    readonly vehicles = computed(() => this.inCompany(this.allVehicles()));
    readonly policies = computed(() => this.inCompany(this.allPolicies()));
    readonly financing = computed(() => this.inCompany(this.allFinancing()));
    readonly cases = computed(() => this.inCompany(this.allCases()));
    readonly events = computed(() => this.inCompany(this.allEvents()));
    readonly tickets = computed(() => this.inCompany(this.allTickets()));
    readonly documents = computed(() => this.inCompany(this.allDocuments()));

    private loadedFor: number | null = null;
    /** Czas ostatniego udanego wczytania list (ms). */
    private loadedAt = 0;
    private refreshing = false;
    private readonly refreshedSubject = new Subject<void>();
    /** Listy odświeżone w tle — strony z własnymi danymi (sprawa) pobierają je ponownie. */
    readonly refreshed$ = this.refreshedSubject.asObservable();
    private documentsRequest: Observable<PanelDocument[]> | null = null;
    /** Zmienia się przy każdym resecie — odpowiedź dla poprzedniego klienta nie trafia do magazynu. */
    private generation = 0;

    /** Ładuje dane, jeśli nie ma ich dla bieżącego klienta. Błąd nie blokuje nawigacji. */
    load(force = false): Observable<boolean> {
        const clientId = this.userService.user?.externalId ?? null;

        if (!force && clientId !== null && clientId === this.loadedFor && !this.failed()) {
            // Dane są — strona wchodzi od razu, starsze niż minuta odświeżają się w tle
            this.refreshIfStale(MAX_AGE_MS);

            return of(true);
        }

        if (clientId !== this.loadedFor) {
            this.reset();
        }

        return this.fetch(clientId).pipe(
            map(() => true),
            catchError(() => {
                this.failed.set(true);

                return of(false);
            })
        );
    }

    /** Odświeżenie w tle, gdy dane są starsze niż `maxAge` (ms); jedno naraz, błąd zostawia poprzednie dane. */
    refreshIfStale(maxAge = MAX_AGE_MS): void {
        const clientId = this.loadedFor;

        if (this.refreshing || clientId === null || this.failed() || Date.now() - this.loadedAt < maxAge) {
            return;
        }

        const generation = this.generation;

        this.refreshing = true;
        this.fetch(clientId)
            .pipe(finalize(() => (this.refreshing = false)))
            .subscribe({
                next: () => {
                    if (generation !== this.generation) {
                        return;
                    }

                    if (this.allDocuments() !== null) {
                        this.loadDocuments(true).subscribe();
                    }

                    this.refreshedSubject.next();
                },
                error: () => undefined,
            });
    }

    private fetch(clientId: number | null): Observable<void> {
        const generation = this.generation;

        return forkJoin({
            me: this.api.me(),
            vehicles: this.api.vehicles(),
            policies: this.api.policies(),
            financing: this.api.financing(),
            cases: this.api.cases(),
            events: this.api.events(20),
            tickets: this.api.tickets(),
        }).pipe(
            tap((data) => {
                // Odpowiedź dla poprzedniego klienta (podgląd administratora) nie trafia do magazynu
                if (generation !== this.generation) {
                    return;
                }

                this.me.set(data.me);
                this.allVehicles.set(data.vehicles);
                this.allPolicies.set(data.policies);
                this.allFinancing.set(data.financing);
                this.allCases.set(data.cases);
                this.allEvents.set(data.events);
                this.allTickets.set(data.tickets);
                this.failed.set(false);
                this.loadedFor = clientId;
                this.loadedAt = Date.now();
            }),
            map(() => undefined)
        );
    }

    loadRisks(): Observable<RiskCategory[]> {
        const cached = this.risks();

        if (cached) {
            return of(cached);
        }

        return this.api.risks().pipe(
            tap((risks) => this.risks.set(risks)),
            catchError(() => of([]))
        );
    }

    /** Jedno zapytanie naraz; po błędzie pusta lista i `documentsFailed` (ekran pokazuje „Spróbuj ponownie”). */
    loadDocuments(force = false): Observable<PanelDocument[]> {
        const cached = this.allDocuments();

        if (cached && !force) {
            return of(cached);
        }

        if (this.documentsRequest && !force) {
            return this.documentsRequest;
        }

        const generation = this.generation;

        this.documentsFailed.set(false);
        this.documentsRequest = this.api.documents().pipe(
            tap((documents) => generation === this.generation && this.allDocuments.set(documents)),
            catchError(() => {
                if (generation === this.generation) {
                    this.documentsFailed.set(true);
                }

                return of([] as PanelDocument[]);
            }),
            finalize(() => (this.documentsRequest = null)),
            shareReplay(1)
        );

        return this.documentsRequest;
    }

    reset(): void {
        this.generation++;
        this.allDocuments.set(null);
        this.documentsFailed.set(false);
        this.documentsRequest = null;
        this.me.set(null);
        this.allVehicles.set(null);
        this.allPolicies.set(null);
        this.allFinancing.set(null);
        this.allCases.set(null);
        this.allEvents.set(null);
        this.allTickets.set(null);
        this.company.set(null);
        this.failed.set(false);
        this.loadedFor = null;
        this.loadedAt = 0;
    }

    /** Wybór firmy z filtra — tylko firma konta, inaczej wszystkie. */
    selectCompany(clientId: number | null): void {
        this.company.set(clientId !== null && this.companies().some((c) => c.clientId === clientId) ? clientId : null);
    }

    /** Firma pozycji do etykiety — tylko na koncie z kilkoma firmami. */
    ownerLabel(owner: Owner | null | undefined): string | null {
        return this.multiCompany() && owner ? owner.shortName : null;
    }

    private inCompany<T extends { owner?: Owner | null }>(items: T[] | null): T[] | null {
        const company = this.company();

        return items && company !== null ? items.filter((item) => item.owner?.clientId === company) : items;
    }
}
